import type { MeetingNotesNode } from './doc-model.js'
import { meetingNotesMarkdownToDoc } from './markdown-to-doc.js'

/**
 * Why a "table" failed to render.
 *
 * - `missing-divider` — the block uses `|` to separate columns but has no
 *   `| --- | --- |` row, so GFM reads it as an ordinary paragraph. This is the
 *   only way a pipe-shaped block can fail; outer pipes are optional.
 * - `whitespace-columns` — no `|` at all: columns aligned with runs of spaces
 *   and underlined with dashes or box-drawing. This is the shape copied out of
 *   the rendered notes panel, and the one that broke production.
 */
export type UnrenderableTableReason = 'missing-divider' | 'whitespace-columns'

export interface UnrenderableTable {
  /** 1-based line of the table's first row, for a message a human can act on. */
  line: number
  reason: UnrenderableTableReason
}

/**
 * A line made only of rule characters — the "underline" half of a table
 * somebody meant to write. Box-drawing and em dashes are here because that is
 * what lands when the rendered panel is copied back into the template editor.
 */
const TABLE_RULE_LINE = /^[\s:|=\-–—―─━═]+$/
/** …and it has to actually draw something, so a bare `|` or `:` is not a rule. */
const TABLE_RULE_INK = /[=\-–—―─━═]/
/** Four spaces or a tab: a code-ish indent, whose alignment means nothing. */
const CODE_INDENT = /^(?: {4}|\t)/
/** Two or more spaces (or a tab) is what turns aligned text into columns. */
const COLUMN_GAP = /\s{2,}|\t+/
/** A row written with the outer pipes GFM does not require but people write. */
const PIPE_ROW_LINE = /^\s*\|.*\|\s*$/
/** Bullet or numbered item, either marker, at any indent. */
const LIST_LINE = /^\s*([-*]|\d+[.)])\s+/

function isTableRuleLine(line: string): boolean {
  return line.trim() !== '' && TABLE_RULE_LINE.test(line) && TABLE_RULE_INK.test(line)
}

/**
 * Does this line read as the header of a table the author meant to write?
 *
 * Used only for the pipe-less shapes: the line has to survive every block the
 * converter would claim first (heading, list, quote), not be an indented
 * code-ish block, and split into at least two columns — by `|`, or by a run of
 * spaces wide enough to be deliberate alignment.
 */
function looksLikeTableHeader(line: string): boolean {
  if (line.trim() === '' || isTableRuleLine(line)) return false
  if (CODE_INDENT.test(line)) return false
  if (/^#{1,6}\s+/.test(line)) return false
  if (LIST_LINE.test(line)) return false
  if (/^\s*>\s?/.test(line)) return false

  const columns = (line.includes('|') ? line.split('|') : line.trim().split(COLUMN_GAP))
    .map((cell) => cell.trim())
    .filter((cell) => cell !== '')
  return columns.length >= 2
}

function containsTable(node: MeetingNotesNode): boolean {
  if (node.type === 'table') return true
  return (node.content ?? []).some(containsTable)
}

/** A maximal run of non-blank lines, with the 1-based number of its first line. */
interface Block {
  lines: string[]
  firstLine: number
}

function splitIntoBlocks(markdown: string): Block[] {
  const lines = markdown.replace(/\r\n?/g, '\n').split('\n')
  const blocks: Block[] = []
  let current: Block | null = null

  for (const [index, line] of lines.entries()) {
    if (line.trim() === '') {
      current = null
      continue
    }
    if (current === null) {
      current = { lines: [], firstLine: index + 1 }
      blocks.push(current)
    }
    current.lines.push(line)
  }

  return blocks
}

/**
 * The line this block's author meant to start a table on, or `null` if nothing
 * in it reads as an attempt at one.
 *
 * Two shapes count, and nothing else:
 *
 * - a row written with outer pipes, which is table syntax whether or not it
 *   parses (`| 任務 | 負責人 |`);
 * - a header-shaped line with a rule line DIRECTLY under it, which is the
 *   copied-out-of-the-panel shape — aligned text over `━━━━`, or pipe-separated
 *   text over `--- | ---`.
 *
 * The adjacency requirement in the second shape is load-bearing. A list that
 * happens to end at a `---` thematic break is otherwise indistinguishable from
 * aligned columns over a rule, and a false positive here BLOCKS A SAVE — far
 * worse than missing an exotic fake table.
 */
function tableIntentLine(block: Block): number | null {
  for (const [index, line] of block.lines.entries()) {
    if (PIPE_ROW_LINE.test(line)) return block.firstLine + index
    const next = block.lines[index + 1]
    if (next !== undefined && looksLikeTableHeader(line) && isTableRuleLine(next)) {
      return block.firstLine + index
    }
  }
  return null
}

/**
 * Find the places a template author meant to write a table and the converter
 * cannot produce one.
 *
 * The bug this exists for: the built-in 行動項目 skeleton held aligned text
 * under a `━` rule instead of markdown, so the renderer emitted a paragraph, and
 * every note generated from the template copied the same non-table. Nothing on
 * the write path noticed for a month.
 *
 * The verdict is RUN, not described: each candidate block goes through
 * `meetingNotesMarkdownToDoc` and is reported when no `table` node comes back.
 * The previous version reimplemented the renderer's table rules in regexes and
 * they drifted — it accepted dividerless pipe rows the renderer turned into a
 * table but GFM does not, and rejected pipe rows without outer pipes that GFM
 * accepts. A guard that asks the converter cannot disagree with it.
 *
 * Pure: no DOM, no framework, safe to run on both the server and the client.
 */
export function findUnrenderableTables(markdown: string): UnrenderableTable[] {
  const problems: UnrenderableTable[] = []

  for (const block of splitIntoBlocks(markdown)) {
    const line = tableIntentLine(block)
    if (line === null) continue

    const doc = meetingNotesMarkdownToDoc(block.lines.join('\n'))
    if (containsTable(doc)) continue

    problems.push({
      line,
      reason: block.lines.some((row) => row.includes('|'))
        ? 'missing-divider'
        : 'whitespace-columns',
    })
  }

  return problems
}
